// web-v2/src/services/admin-users.service.js
//
// The owner's doors (personal-logins, PLAN R2, frozen): the TRACON users and the shared-login
// switch. The server is the gate on every one (owner only; the shared login only in the bootstrap
// window); these only send exactly the frozen fields. Relative imports so
// tests/users-page-shapes.test.js runs it under node.

import { api } from '../lib/api.js';

export const USERS_PATH = '/api/tracon/admin/users';
export const SHARED_LOGIN_PATH = '/api/tracon/admin/auth/shared-login';
export const ROLES = Object.freeze(['owner', 'producer', 'demo']);
export const SWITCH_MODES = Object.freeze(['on', 'notify', 'off']);
const PATCH_FIELDS = ['display_name', 'role', 'person_id'];

// The answers the pages word themselves (no generic API toast on top): the users writer's own codes
// (services/tracon-users.service.js fail()) and the owner gate's.
export const USERS_WORDS = Object.freeze({
  owner_only: 'Only the owner, signed in with Google, can change users.',
  last_owner: 'TRACON must keep at least one active owner.',
  self_disable: 'You cannot disable your own sign-in.',
  email_taken: 'That address already has a TRACON user.',
  email_domain: 'An owner or producer signs in with an ndemnifly.com address.',
  email_immutable: 'An address never changes. Add a new user for the new address.',
  invalid_email: 'That is not an email address.',
  invalid_role: 'Pick owner, producer or demo.',
  display_name_required: 'A name is required.',
  reason_required: 'Say why, so the record shows it.',
  not_found: 'That user is no longer there. Reload the list.',
  identity_unavailable: 'The users list could not be read right now. Try again shortly.',
  invalid_person_id: 'That is not a person record. Pick the person again, or leave them unlinked.',
  person_unknown: 'That person record is no longer there. Pick the person again, or leave them unlinked.',
  query_too_short: 'Type at least two letters of a name or email.',
  store_unavailable: 'The person records could not be read right now. Try again shortly.',
  users_unreadable: 'The users store could not be read right now. Try again shortly.',
  users_store_error: 'The users store did not take that change. Nothing was saved; try again shortly.',
  internal_error: 'The server could not finish that. Reload the list to see what is there.',
  sub_bound_elsewhere: 'That Google account is already tied to another TRACON user. Reset that user\'s Google sign-in first.',
  sub_mismatch: 'That address is tied to a different Google account. Reset its Google sign-in first.',
  invalid_sub: 'Google did not name an account for that sign-in.',
  invalid_surface: 'The sign-in surface was not recognised.',
  owner_has_all: 'An owner holds every privilege. Change the role to change them.',
  invalid_permissions: 'One of those boxes is not a TRACON privilege. Reload the page and try again.',
});
// The writer's own statuses (services/tracon-users.service.js fail(); internal_error is the door's 500).
const USERS_STATUS = { owner_only: 403, owner_has_all: 409, last_owner: 409, self_disable: 409, email_taken: 409, not_found: 404, identity_unavailable: 503,
  users_unreadable: 503, users_store_error: 503, store_unavailable: 503, internal_error: 500, sub_bound_elsewhere: 409, sub_mismatch: 409 };
export const USERS_EXPECTED = Object.freeze(Object.keys(USERS_WORDS).map((code) => ({ status: USERS_STATUS[code] || 400, code })));
/** Words for a users-door failure; `read` words an unknown failure of the list read as a read, not a save. */
export const usersWords = (e, { read = false } = {}) => USERS_WORDS[e && e.body && e.body.code]
  || (read ? 'The users list could not be read. Try again shortly.' : 'That did not save. Try again.');
export const SWITCH_EXPECTED = Object.freeze([
  { status: 403, code: 'owner_only' },
  { status: 409, code: 'confirm_required' },
  { status: 409, code: 'owner_google_unproven' },
  { status: 503, code: 'personal_login_unavailable' },
  { status: 503, code: 'identity_unavailable' },
]);

const one = (id, tail = '') => `${USERS_PATH}/${encodeURIComponent(id)}${tail}`;
const opts = { expected: USERS_EXPECTED };

export function listUsers() { return api.get(`${USERS_PATH}?_t=${Date.now()}`, opts); }

export function createUser({ email, display_name, role, person_id } = {}) {
  const body = { email, display_name, role };
  if (person_id) body.person_id = person_id;
  return api.post(USERS_PATH, body, opts);
}

/** Sends only the keys present in `patch` (null clears a person link); never status. */
export function updateUser(id, patch = {}) {
  const body = {};
  for (const k of PATCH_FIELDS) if (patch[k] !== undefined) body[k] = patch[k];
  return api.patch(one(id), body, opts);
}

export async function disableUser(id, reason) {
  const why = String(reason || '').trim();
  if (!why) throw new Error('a reason is required to disable a user');
  return api.post(one(id, '/disable'), { reason: why }, opts);
}
export function enableUser(id) { return api.post(one(id, '/enable'), {}, opts); }
/** The person picker (final-fix COMPLETE-9): GET /users/person-search?q= -> { people: [{ person_id, name, email, crm_contact_id, license_count }] }. */
export function searchPeopleToLink(q) { return api.get(`${USERS_PATH}/person-search?q=${encodeURIComponent(String(q || '').trim())}`, opts); }
/** The boxes (David 2026-10-27): PATCH /users/:id/permissions { permissions: [keys] }. Owner only at the server. */
export function setUserPermissions(id, permissions) {
  if (!Array.isArray(permissions)) throw new Error('permissions must be a list of privilege keys');
  return api.patch(one(id, '/permissions'), { permissions }, opts);
}
export function resetGoogle(id) { return api.post(one(id, '/reset-google'), {}, opts); }

export function getSharedLogin() { return api.get(`${SHARED_LOGIN_PATH}?_t=${Date.now()}`, { expected: SWITCH_EXPECTED }); }

export async function setSharedLogin(mode, confirm = false) {
  if (!SWITCH_MODES.includes(mode)) throw new Error(`unknown mode ${mode}`);
  return api.post(SHARED_LOGIN_PATH, confirm ? { mode, confirm: true } : { mode }, { expected: SWITCH_EXPECTED });
}

// THE SWITCH PAGE'S WORDS for the server's answer (reviews L5-UX-1, L5-UX-6). L2's sharedUse() returns
// routes_7d as [{route, uses, last_at}] and origins_7d as [{origin, ip, user_agent, uses, last_at}];
// a plain string still reads. `fmt` formats a timestamp (the page passes formatDateTime).
const countTail = (x, fmt) => (Number.isFinite(x.uses) ? ` (${x.uses}${x.last_at ? `, last ${fmt(x.last_at)}` : ''})` : '');
function countList(xs, name, fmt) {
  if (!Array.isArray(xs) || !xs.length) return 'none';
  return xs.map((x) => (x && typeof x === 'object' ? `${name(x) || 'unknown'}${countTail(x, fmt)}` : String(x))).join('; ');
}
export const sharedRoutesText = (xs, fmt = String) => countList(xs, (x) => x.route, fmt);
export const sharedOriginsText = (xs, fmt = String) => countList(xs, (x) => [x.origin, x.ip, x.user_agent].filter(Boolean).join(' '), fmt);

/** One sentence per reason a 409 confirm_required carries (services/shared-login-flip.service.js flip). */
export function confirmReasonLines(body) {
  const reasons = body && Array.isArray(body.reasons) ? body.reasons : [];
  return reasons.map((r) => {
    if (r === 'shared_login_used') return `The shared login was used${Number.isFinite(body.uses_7d) ? ` ${body.uses_7d} times` : ''} in the last 7 days; those callers stop working.`;
    if (r === 'shared_use_unknown') return 'The shared login\'s use in the last 7 days could not be counted, so whether anyone still relies on it is unknown.';
    if (r === 'no_standalone_owner_sign_in') return 'No owner has signed in with Google on the installed app yet; test the phone first.';
    return `The server asked for confirmation (${r}).`;
  });
}
